import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiFilter, FiCalendar, FiUser, FiPhone, FiMail, FiMessageSquare, FiClock } from 'react-icons/fi';
import { activitiesAPI, settingsAPI } from '../../services/api';
import StatusBadge from '../../components/Common/StatusBadge';
import { toast } from 'react-toastify';
import Modal from '../../components/Common/Modal';
import AuditTimeline from '../../components/Common/AuditTimeline';

const ACTIVITY_TYPES = ['Call', 'Email', 'WhatsApp', 'Meeting', 'Follow-up'];

const ActivityList = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', activity_type: '', date_from: '', date_to: '' });
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyActivity, setHistoryActivity] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);

  const activityTypeLabel = (v) => ({
    'Call': t('activities.types.call'),
    'Email': t('activities.types.email'),
    'WhatsApp': t('activities.types.whatsApp'),
    'Meeting': t('activities.types.meeting'),
    'Follow-up': t('activities.types.followUp'),
  })[v] || v;

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: 25 };
      if (filters.search) params.search = filters.search;
      if (filters.activity_type) params.activity_type = filters.activity_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await activitiesAPI.getAll(params);
      setActivities(res.data.activities || []);
      setPagination(p => ({ ...p, total: res.data.total || 0, pages: res.data.pages || 0 }));
    } catch (err) { toast.error(t('activities.toasts.loadFailed')); }
    setLoading(false);
  }, [pagination.page, filters]);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  const openHistory = async (a) => {
    if (!a?.activity_id) return;
    setHistoryActivity(a);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await settingsAPI.getAuditLog({
        entity_type: 'activity',
        entity_id: a.activity_id,
        limit: 200,
        page: 1
      });
      setHistoryLogs(res.data || []);
    } catch (err) {
      setHistoryLogs([]);
      toast.error(t('activities.toasts.historyFailed'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'Call': return <FiPhone />;
      case 'Email': return <FiMail />;
      case 'WhatsApp': return <FiMessageSquare />;
      case 'Meeting': return <FiUser />;
      default: return <FiCalendar />;
    }
  };

  const getIconBg = (type) => {
    const colors = {
      'Call': 'var(--info-light)', 'Email': 'var(--danger-light)',
      'WhatsApp': 'var(--success-light)', 'Meeting': 'var(--warning-light)',
      'Follow-up': 'var(--purple-light)'
    };
    return colors[type] || 'var(--bg-secondary)';
  };

  const getIconColor = (type) => {
    const colors = {
      'Call': 'var(--info)', 'Email': 'var(--danger)',
      'WhatsApp': 'var(--success)', 'Meeting': 'var(--warning)',
      'Follow-up': 'var(--purple)'
    };
    return colors[type] || 'var(--text-muted)';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('activities.title')}</h2>
          <p className="page-subtitle">{t('activities.activitiesLogged', { count: pagination.total })}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="search-box">
          <FiSearch />
          <input placeholder={t('activities.searchPlaceholder')} value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && loadActivities()} />
        </div>
        <select value={filters.activity_type} onChange={(e) => setFilters(f => ({ ...f, activity_type: e.target.value }))}>
          <option value="">{t('activities.allTypes')}</option>
          {ACTIVITY_TYPES.map(at => <option key={at} value={at}>{activityTypeLabel(at)}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        <button className="btn btn-outline btn-sm" onClick={() => { setFilters({ search: '', activity_type: '', date_from: '', date_to: '' }); }}>
          <FiFilter /> {t('common.clear')}
        </button>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div className="timeline">
          {activities.map(a => (
            <div className="timeline-item" key={a.activity_id} style={{ display: 'flex', gap: 16, padding: '16px 20px' }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', background: getIconBg(a.activity_type),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: getIconColor(a.activity_type), flexShrink: 0, fontSize: 16
              }}>
                {getIcon(a.activity_type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <StatusBadge status={a.activity_type} />
                    <span style={{ marginLeft: 8, fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
                      {a.lead_name && `${t('activities.leadPrefix')}${a.lead_name}`}
                      {(a.student_name_full || a.student_name) && `${t('activities.studentPrefix')}${a.student_name_full || a.student_name}`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FiCalendar size={12} /> {new Date(a.activity_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} {a.activity_time}
                    </div>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => openHistory(a)}>
                      <FiClock size={12} /> {t('common.history')}
                    </button>
                  </div>
                </div>
                <p style={{ margin: '8px 0 4px', fontSize: 13, color: 'var(--text-secondary)' }}>{a.notes}</p>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  <span>{t('activities.byPrefix')}{a.created_by_name}</span>
                  {a.next_followup && <span style={{ color: 'var(--orange)', fontWeight: 600 }}>{t('activities.nextFollowup')}{new Date(a.next_followup).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>}
                </div>
              </div>
            </div>
          ))}
          {activities.length === 0 && <div className="empty-state"><p>{t('activities.noActivities')}</p></div>}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>{t('common.previous')}</button>
          <span>{t('common.page', { current: pagination.page, total: pagination.pages })}</span>
          <button disabled={pagination.page >= pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>{t('common.next')}</button>
        </div>
      )}

      <Modal
        isOpen={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryActivity(null);
          setHistoryLogs([]);
          setHistoryLoading(false);
        }}
        title={historyActivity ? t('activities.historyTitle', { type: historyActivity.activity_type || '' }) : t('activities.historyTitleFallback')}
        size="lg"
        footer={<button className="btn btn-outline" onClick={() => setHistoryOpen(false)}>{t('common.close')}</button>}
      >
        {historyLoading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <AuditTimeline logs={historyLogs} showEntity={false} />
        )}
      </Modal>
    </div>
  );
};

export default ActivityList;
